import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Search } from 'lucide-react';
import { ArrowRight } from 'lucide-react';
import QoreTalksShell, { fmtViews } from './QoreTalksShell';
import EpisodeCard from './EpisodeCard';
import FeaturedEpisode from './FeaturedEpisode';
import LiveRoomCard from './LiveRoomCard';
import QtEmptyState from './QtEmptyState';
import TalkFormModal from './TalkFormModal';
import { useAuth } from '../../context/AuthContext';
import { fetchTalks, fetchLiveRooms } from '../../services/qoreTalksApi';
import { QORE_TALK_CATEGORIES, USE_MOCKS } from '../../constants/qoreTalks';
import {
  applyTalkFilters,
  pickFeaturedTalk,
  getListeningProgress,
} from './qoreTalksUtils';

const MOCK_TALKS = USE_MOCKS
  ? [
      {
        _id: 't1',
        title: 'Building AI Products in 2025',
        category: 'AI',
        status: 'published',
        duration: 52,
        host: { name: 'Dr. Sarah Chen' },
        analytics: { views: 1240 },
        publishedAt: '2025-11-01',
        thumbnailUrl: '',
      },
      {
        _id: 't2',
        title: 'Scaling Your Startup to $1M ARR',
        category: 'Entrepreneurship',
        status: 'published',
        duration: 45,
        host: { name: 'Marcus Williams' },
        analytics: { views: 876 },
        publishedAt: '2025-10-22',
        thumbnailUrl: '',
      },
    ]
  : [];

const fmtClock = (s) => {
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, '0')}`;
};

const QoreTalksDashboard = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isAdmin, isInstructor } = useAuth();

  const [talks, setTalks] = useState([]);
  const [liveRooms, setLiveRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterTab, setFilterTab] = useState(0);
  const [category, setCategory] = useState('all');
  const [search, setSearch] = useState('');
  const [likedIds, setLikedIds] = useState(new Set());
  const [showModal, setShowModal] = useState(false);
  const [stats, setStats] = useState({ total: 0, views: 0, speakers: 0 });

  const FILTER_LABELS = [
    t('qoreTalks.allFilter'),
    t('qoreTalks.liveFilter'),
    t('qoreTalks.recordedFilter'),
    t('qoreTalks.upcomingFilter'),
  ];

  const fetchData = useCallback(async () => {
    try {
      const [published, scheduled, rooms] = await Promise.all([
        fetchTalks({ limit: 50 }).catch(() => []),
        fetchTalks({ status: 'scheduled', limit: 20 }).catch(() => []),
        fetchLiveRooms().catch(() => []),
      ]);
      const merged = [
        ...published,
        ...scheduled.filter((s) => !published.find((p) => p._id === s._id)),
      ];
      const talksData = merged.length > 0 ? merged : MOCK_TALKS;
      setTalks(talksData);
      setLiveRooms(rooms);
      setStats({
        total: talksData.filter((x) => x.status === 'published').length,
        views: talksData.reduce((s, x) => s + (x.analytics?.views || 0), 0),
        speakers: new Set(talksData.map((x) => x.host?.name).filter(Boolean)).size,
      });
    } catch {
      setTalks(MOCK_TALKS);
      setLiveRooms([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const interval = setInterval(() => {
      fetchLiveRooms().then(setLiveRooms).catch(() => {});
    }, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const featured = useMemo(() => pickFeaturedTalk(talks), [talks]);
  const filteredTalks = useMemo(
    () =>
      applyTalkFilters(talks, {
        tabIndex: filterTab,
        category,
        search,
        liveRoomCount: liveRooms.length,
      }),
    [talks, filterTab, category, search, liveRooms.length]
  );

  const gridTalks = useMemo(() => {
    if (!featured) return filteredTalks;
    const fid = featured._id || featured.id;
    return filteredTalks.filter((t) => (t._id || t.id) !== fid);
  }, [filteredTalks, featured]);

  const progress = getListeningProgress();

  const handleLike = (id) =>
    setLikedIds((prev) => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });

  const handlePlay = (talk) => {
    if (talk.isLive || talk.status === 'live') {
      navigate('/qoretalks/live/' + (talk.liveRoomId || talk._id));
      return;
    }
    navigate('/qoretalks/episodes/' + (talk._id || talk.id));
  };

  const liveStrip =
    liveRooms.length > 0 ? (
      <div className="qt-live-strip" role="status" aria-live="polite">
        <div className="qt-live-strip__inner">
          <span className="qt-live-strip__label">
            <span className="qt-nav__dot" aria-hidden /> {t('qoreTalks.liveNow')}
          </span>
          {liveRooms.slice(0, 3).map((room) => (
            <Link
              key={room._id}
              to={`/qoretalks/live/${room.roomId || room._id}`}
              className="qt-btn qt-btn--outline-rose"
              style={{ fontSize: '0.8rem', padding: '0.4rem 0.85rem' }}
            >
              {room.title} · {room.participantCount || 0}
            </Link>
          ))}
        </div>
      </div>
    ) : null;

  const emptyVariant =
    search || category !== 'all' ? 'search' : filterTab === 1 ? 'live' : 'episodes';

  return (
    <QoreTalksShell
      activeTab="home"
      liveStrip={liveStrip}
      hero={{
        badge: t('services.comingQ12026'),
        title: t('qoreTalks.heroTitle'),
        titleAccent: t('qoreTalks.heroAccent'),
        subtitle: t('qoreTalks.pageSubtitle'),
        actions: (
          <>
            <Link to="/qoretalks/live" className="qt-btn qt-btn--outline-rose">
              {t('qoreTalks.joinLive')}
            </Link>
            <Link to="/qoretalks/apply" className="qt-btn qt-btn--ghost">
              {t('qoreTalks.applyToSpeak')}
            </Link>
            <Link to="/qoretalks/subscribe" className="qt-btn qt-btn--ghost">
              {t('services.subscribeForUpdates')}
            </Link>
            {(isAdmin || isInstructor) && (
              <>
                <button
                  type="button"
                  className="qt-btn qt-btn--rose"
                  onClick={() => setShowModal(true)}
                >
                  {t('qoreTalks.addTalkBtn')}
                </button>
                <Link to="/qoretalks/manage" className="qt-btn qt-btn--ghost">
                  {t('qoreTalks.manageTitle')}
                </Link>
              </>
            )}
          </>
        ),
        stats: (
          <>
            <div>
              <div className="qt-hero__stat-value">{stats.total || MOCK_TALKS.length}</div>
              <div className="qt-hero__stat-label">{t('qoreTalks.statEpisodes')}</div>
            </div>
            <div>
              <div className="qt-hero__stat-value">{fmtViews(stats.views)}</div>
              <div className="qt-hero__stat-label">{t('qoreTalks.statViews')}</div>
            </div>
            <div>
              <div className="qt-hero__stat-value">{stats.speakers}</div>
              <div className="qt-hero__stat-label">{t('qoreTalks.statSpeakers')}</div>
            </div>
            <div>
              <div className="qt-hero__stat-value">{liveRooms.length}</div>
              <div className="qt-hero__stat-label">{t('qoreTalks.statLiveNow')}</div>
            </div>
          </>
        ),
      }}
    >
      {progress?.episodeId && (
        <div className="qt-continue">
          <div>
            <strong>{t('qoreTalks.continueListening')}</strong>
            <p style={{ margin: '0.25rem 0 0', fontSize: '0.8rem', color: '#94a3b8' }}>
              {t('qoreTalks.continueFrom', { time: fmtClock(progress.currentTime || 0) })}
            </p>
          </div>
          <button
            type="button"
            className="qt-btn qt-btn--primary"
            onClick={() => navigate('/qoretalks/episodes/' + progress.episodeId)}
          >
            {t('qoreTalks.watchNow')}
          </button>
        </div>
      )}

      {!loading && featured && filterTab !== 1 && !search && category === 'all' && (
        <FeaturedEpisode talk={featured} onPlay={handlePlay} />
      )}

      <div className="qt-toolbar">
        <div className="qt-search">
          <Search className="qt-search__icon" size={18} aria-hidden />
          <input
            type="search"
            className="qt-search__input"
            placeholder={t('qoreTalks.searchPlaceholder')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label={t('qoreTalks.searchPlaceholder')}
          />
        </div>
        <div className="qt-categories" role="group" aria-label={t('qoreTalks.allCategories')}>
          <button
            type="button"
            className={`qt-cat-pill${category === 'all' ? ' qt-cat-pill--active' : ''}`}
            onClick={() => setCategory('all')}
          >
            {t('qoreTalks.allCategories')}
          </button>
          {QORE_TALK_CATEGORIES.filter((c) =>
            talks.some((t) => t.category === c)
          ).map((cat) => (
            <button
              key={cat}
              type="button"
              className={`qt-cat-pill${category === cat ? ' qt-cat-pill--active' : ''}`}
              onClick={() => setCategory(cat)}
            >
              {cat}
            </button>
          ))}
        </div>
      </div>

      <div className="qt-filters" role="tablist">
        {FILTER_LABELS.map((label, i) => (
          <button
            key={label}
            type="button"
            role="tab"
            aria-selected={filterTab === i}
            className={`qt-filter${filterTab === i ? ' qt-filter--active' : ''}`}
            onClick={() => setFilterTab(i)}
          >
            {label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="qt-grid">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div className="qt-skeleton" key={i}>
              <div className="qt-skeleton__thumb" />
              <div className="qt-skeleton__body">
                <div className="qt-skeleton__line" style={{ width: '30%' }} />
                <div className="qt-skeleton__line" style={{ width: '90%' }} />
                <div className="qt-skeleton__line" style={{ width: '60%' }} />
              </div>
            </div>
          ))}
        </div>
      ) : filterTab === 1 && liveRooms.length > 0 ? (
        <div className="qt-live-grid qt-motion-grid">
          {liveRooms.map((room) => (
            <LiveRoomCard
              key={room._id}
              room={room}
              onJoin={(r) => navigate('/qoretalks/live/' + (r.roomId || r._id))}
            />
          ))}
        </div>
      ) : gridTalks.length === 0 && !(filterTab === 1 && liveRooms.length > 0) ? (
        <QtEmptyState
          variant={emptyVariant}
          actions={
            <>
              {filterTab === 1 && (
                <Link to="/qoretalks/live" className="qt-btn qt-btn--rose">
                  {t('qoreTalks.joinLive')}
                </Link>
              )}
              {(search || category !== 'all') && (
                <button
                  type="button"
                  className="qt-btn qt-btn--ghost"
                  onClick={() => {
                    setSearch('');
                    setCategory('all');
                  }}
                >
                  {t('qoreTalks.allCategories')}
                </button>
              )}
              <Link to="/qoretalks/subscribe" className="qt-btn qt-btn--primary">
                {t('services.subscribeForUpdates')}
              </Link>
            </>
          }
        />
      ) : (
        <div className="qt-grid qt-motion-grid">
          {gridTalks.map((talk) => (
            <EpisodeCard
              key={talk._id || talk.id}
              talk={talk}
              onPlay={handlePlay}
              onLike={handleLike}
              liked={likedIds.has(talk._id || talk.id)}
            />
          ))}
        </div>
      )}

      <div className="qt-cta-panel">
        <h3>{t('qoreTalks.guestCtaTitle')}</h3>
        <p>{t('qoreTalks.guestCtaDesc')}</p>
        <Link to="/qoretalks/apply" className="qt-btn qt-btn--primary qt-btn--lg">
          {t('qoreTalks.applyToSpeak')}
          <ArrowRight size={18} />
        </Link>
      </div>

      {showModal && (
        <TalkFormModal
          isOpen={showModal}
          onClose={() => setShowModal(false)}
          onCreated={fetchData}
        />
      )}
    </QoreTalksShell>
  );
};

export default QoreTalksDashboard;